'use client';

import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import logMultiDayRouteTagClicked from '@alltrails/amplitude/events/logMultiDayRouteTagClicked';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import TableRow from '@alltrails/denali/components/TableRow';
import Backpacking from '@alltrails/denali/icons/Backpacking';
import type TrailDetails from '@alltrails/types/TrailDetails';
import styles from './ParentTrails.module.scss';

const ParentTrailsModal = dynamic(() => import('./ParentTrailsModal'), { ssr: false });

type ParentTrailsProps = {
  trails: NonNullable<TrailDetails['parentTrails']>;
  trailId: number;
};

// TODO: Translate (DISCO-24081)
// https://www.figma.com/design/B7oyzdXDXbRUGayrIwJnRp/Adv-Planning-2026--Handoff-?node-id=2788-37034&t=QdvY1ccj4HKq2Qkj-4
const ParentTrails = ({ trails, trailId }: ParentTrailsProps) => {
  const languageRegionCode = useLanguageRegionCode();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalHasOpened, setModalHasOpened] = useState(false);

  const trailIds = useMemo(() => trails.map(trail => trail.id), [trails]);

  if (trails.length === 0) {
    return null;
  }

  if (trails.length === 1) {
    const { name, slug, id } = trails[0];
    const onClick = () => {
      logTrailCardClicked({
        card_location: 'web trail page',
        detailed_card_location: 'trail details part of multi day route',
        trail_id: String(id)
      });
      logMultiDayRouteTagClicked({
        destination_trail_id: id,
        trail_id: trailId
      });
    };
    return (
      <a className={styles.container} href={wrapUrlSafe(`/trail/${slug}`, languageRegionCode)} onClick={onClick} target="_blank">
        <TableRow
          label="Part of a multi-day route"
          description={name}
          leftContent={
            <div className={styles.iconContainer}>
              <Backpacking color="--color-text-secondary" />
            </div>
          }
        />
      </a>
    );
  }

  return (
    <>
      <button
        className={styles.container}
        onClick={() => {
          setModalHasOpened(true);
          setIsModalOpen(true);
        }}
      >
        <TableRow
          label={`Part of ${trails.length} multi-day routes`}
          description={`${trails[0].name} +${trails.length - 1} more`}
          leftContent={
            <div className={styles.iconContainer}>
              <Backpacking color="--color-text-secondary" />
            </div>
          }
        />
      </button>
      {modalHasOpened && (
        <ParentTrailsModal isOpen={isModalOpen} onCloseRequest={() => setIsModalOpen(false)} currentTrailId={trailId} trailIds={trailIds} />
      )}
    </>
  );
};

export default ParentTrails;
